/**
 * 액션 결과 판정 보조 — 전부 순수 함수라 훅 없이 단위 검증된다.
 * 어떤 액션이든 같은 규칙으로 "폐기됐나 · 실패했나 · 무엇으로 기록하나" 를 정한다.
 */
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import {
  type ActionTarget,
  type Discarded,
  type Failure,
} from '../types/questionCardAi.types';

import { type QuestionCardAiTrackedResult } from './events.quiz-question-card-ai';
import { isQuestionCardAiHttpError } from './questionCardAiHttpError';

/** 결과가 너무 빨리 오면 스켈레톤이 깜빡이기만 한다 (PRD §7-6). 최소 노출 시간까지 기다린다. */
export const waitAtLeast = (startedAt: number, minMs: number) => {
  const remaining = minMs - (Date.now() - startedAt);
  return remaining > 0
    ? new Promise<void>(resolve => setTimeout(resolve, remaining))
    : Promise.resolve();
};

type TerminalOutcome = {
  result: QuestionCardAiTrackedResult;
  errorCode: string;
  discardReason: string;
};

/** terminal 이벤트의 result 와 사유를 한 곳에서 정한다. 사유 칸은 해당 result 일 때만 채운다. */
export const terminalOutcome = (
  failure: Failure,
  discarded: Discarded,
): TerminalOutcome => {
  if (failure)
    return failure.cancelled
      ? { result: 'cancel', errorCode: '', discardReason: '' }
      : { result: 'fail', errorCode: failure.code, discardReason: '' };
  if (discarded)
    return { result: 'discard', errorCode: '', discardReason: discarded };
  return { result: 'success', errorCode: '', discardReason: '' };
};

/**
 * 적용 시점에 "요청을 만든 그 문항이 맞는지" 판정할 지문.
 * 카드 id 만 보면, 요청 중 사용자가 문제나 기존 정답을 고쳐도 옛 응답이 그대로 적용된다.
 */
export const resolveApplyGuard = (
  current: ActionTarget | null,
  requestedRevision: string,
  revisionOf: (quiz: BuildQuizListItem) => string,
): Discarded =>
  !current
    ? 'source_removed'
    : revisionOf(current.quiz) !== requestedRevision
    ? 'source_changed'
    : null;

const errorNameOf = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'name' in error
    ? String(error.name)
    : '';

/**
 * 서버 응답이 아닌 실패는 오류 이름으로 분류한다. instanceof DOMException 은 realm 이
 * 다르면(jsdom·iframe) 거짓이라 이름으로 판정한다.
 */
const CLIENT_ERROR_CODE_BY_NAME = new Map<string, string>([
  ['TimeoutError', 'CLIENT_TIMEOUT'],
  ['AbortError', 'ABORTED'],
  // 서버 응답이 wire 계약(questionCardAiEndpoints 의 응답 스키마)을 어겼다.
  ['QuestionCardAiInvalidResponseError', 'INVALID_RESPONSE'],
]);

export const errorCodeOf = (error: unknown): string =>
  isQuestionCardAiHttpError(error)
    ? error.code ?? `HTTP_${error.status}`
    : CLIENT_ERROR_CODE_BY_NAME.get(errorNameOf(error)) ?? 'UNKNOWN';
